import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParallax } from 'react-scroll-parallax';
import { Link } from 'react-router-dom';
import './ServicePages.css';

const PredictiveAnalytics = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const headerParallax = useParallax({
    translateY: [0, 50],
    opacity: [1, 0.5],
    scale: [1, 0.9],
    easing: 'easeInQuad',
    startScroll: 0,
    endScroll: 600
  });

  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    },
    hover: {
      scale: 1.05,
      boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.3)",
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 15
      }
    }
  };

  const scaleVariants = {
    hover: {
      scale: 1.05,
      boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 15
      }
    }
  };

  return (
    <div className="service-page">
      {/* Hero Section */}
      <section className="service-hero predictive-analytics-hero" ref={headerParallax.ref}>
        <div className="service-hero-overlay"></div>
        <div className="service-hero-content">
          <h1>Predictive Analytics</h1>
          <p>Leveraging data to forecast trends and make informed business decisions</p>
        </div>
      </section>

      {/* Overview Section */}
      <motion.section 
        className="service-overview"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <motion.h2 variants={itemVariants}>What is Predictive Analytics?</motion.h2>
        <motion.p variants={itemVariants}>
          Predictive Analytics uses historical data, statistical algorithms, and machine learning techniques to identify 
          the likelihood of future outcomes. At Hyperion AI, we develop sophisticated predictive models that help businesses 
          forecast trends, anticipate customer behavior, optimize operations, and make data-driven decisions that drive growth 
          and competitive advantage.
        </motion.p>
      </motion.section>

      {/* Use Cases Section */}
      <motion.section 
        className="service-use-cases"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <motion.h2 variants={itemVariants}>Use Cases</motion.h2>
        <motion.div 
          className="use-cases-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <motion.div 
            className="use-case-card predictive-analytics-card-1"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3>Demand Forecasting</h3>
            <p>Accurate prediction of product demand to optimize inventory levels and supply chain operations.</p>
          </motion.div>
          
          <motion.div 
            className="use-case-card predictive-analytics-card-2"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3>Financial Forecasting</h3>
            <p>Projection of financial performance to inform budgeting, investment decisions, and risk management.</p>
          </motion.div>
          
          <motion.div 
            className="use-case-card predictive-analytics-card-3"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3>Customer Behavior Analysis</h3>
            <p>Prediction of customer actions, preferences, and lifetime value to enhance marketing and retention strategies.</p>
          </motion.div>
          
          <motion.div 
            className="use-case-card predictive-analytics-card-4"
            variants={cardVariants}
            whileHover="hover"
          >
            <div className="use-case-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <h3>Predictive Maintenance</h3>
            <p>Forecasting equipment failures before they occur to reduce downtime and maintenance costs.</p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Technology Section */}
      <motion.section 
        className="service-technology"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <motion.h2 variants={itemVariants}>Our Technology</motion.h2>
        <motion.p variants={itemVariants}>
          Hyperion AI combines advanced statistical methods, machine learning algorithms, and domain expertise to deliver predictive analytics solutions that drive measurable business outcomes.
        </motion.p>
        <motion.div className="technology-features" variants={containerVariants}>
          <motion.div className="technology-feature" variants={itemVariants}>
            <h3>Machine Learning Models</h3>
            <p>Sophisticated regression, classification, and time series models that learn from historical data to predict future events.</p>
          </motion.div>
          <motion.div className="technology-feature" variants={itemVariants}>
            <h3>Real-time Analytics</h3>
            <p>Stream processing capabilities that enable continuous analysis and prediction based on incoming data.</p>
          </motion.div>
          <motion.div className="technology-feature" variants={itemVariants}>
            <h3>Explainable AI</h3>
            <p>Transparent models that provide insights into prediction factors, enabling trust and informed decision-making.</p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* CTA Section */}
      <section className="service-cta">
        <div className="service-cta-content">
          <h2>Ready to Leverage Predictive Analytics?</h2>
          <p>Contact our team to discuss how our predictive analytics solutions can help your business make data-driven decisions.</p>
          <Link to="/contact-us" className="cta-button">Get Started</Link>
        </div>
      </section>
    </div>
  );
};

export default PredictiveAnalytics;
